import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import InterviewCaptureActions from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Shared/InterviewActions/InterviewCaptureActions';
import OutboxItemCardActions from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Shared/InterviewActions/OutboxItemCardActions';
import type InternalInterviewListEntryView from '@UI/Pages/Beneficiaries/Hooks/useBeneficiaryInterviewsList/InternalInterviewListEntryView';
import useLocalInterviewPort from '@UI/Providers/PortsProvider/Hooks/LocalInterviews/useLocalInterviewPort';
import useOutboxItems from '@UI/Providers/PortsProvider/Hooks/OutboxRunner/useOutboxItems';
import * as React from 'react';

type Props = {
	readonly entry: InternalInterviewListEntryView;
	readonly subject: SubjectAddress;
};

/**
 * The existing actions for a recording kept on this device: resume or delete a capture never carried
 * through, retry or abandon a transfer that failed. Renders nothing when the entry offers neither.
 */
const LocalRecordingActions: React.FC<Props> = ({ entry, subject }) => {
	const localInterviewPort = useLocalInterviewPort();
	const outboxItems = useOutboxItems();

	const handleDiscarded = React.useCallback(() => {
		if (entry.localInterviewId !== null) {
			void localInterviewPort.remove(entry.localInterviewId);
		}
	}, [localInterviewPort, entry.localInterviewId]);

	const resumableLocalInterviewId = entry.resumableCaptureLocalInterviewId;

	if (resumableLocalInterviewId !== null) {
		return (
			<InterviewCaptureActions
				subject={subject}
				localInterviewId={resumableLocalInterviewId}
				isNotes={entry.isNotes}
			/>
		);
	}

	const queueActionTarget = entry.queueActionTarget;

	if (queueActionTarget === null) {
		return null;
	}

	const outboxItem = outboxItems.find((item) => item.id === queueActionTarget.outboxItemId);

	if (outboxItem === undefined || outboxItem.state !== 'failed') {
		return null;
	}

	return (
		<OutboxItemCardActions
			item={outboxItem}
			conductedOnDisplay={entry.conductedOnDisplay}
			isNotes={entry.isNotes}
			hasServerInterview={entry.hasServerInterview}
			onDiscarded={handleDiscarded}
		/>
	);
};

export default LocalRecordingActions;
